import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, InputNumber, DatePicker, Space, Card, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Condition, ConditionType, CONDITION_CONFIGS } from '../../types/conditionTypes';
import styles from './ClientSegmentConditionBuilder.module.scss';

interface ClientSegmentConditionBuilderProps {
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  availableOptions: {
    sexTypes: any[];
    channels: any[];
    brands: any[];
    categories: any[];
    levels: any[];
    countries?: any[];
    statesByCountry?: any[];
  };
  onSearchBrands?: (value: string) => void;
  onSearchCategories?: (value: string) => void;
  onSearchLevels?: (value: string) => void;
  onFetchStatesByCountry?: (countryId: number) => void;
}

const ClientSegmentConditionBuilder: React.FC<ClientSegmentConditionBuilderProps> = ({
  conditions,
  onConditionsChange,
  availableOptions,
  onSearchBrands,
  onSearchCategories,
  onSearchLevels,
  onFetchStatesByCountry
}) => {
  const { t } = useTranslation();

  const addCondition = (type: ConditionType) => {
    const newCondition: Condition = {
      id: Date.now().toString(),
      type,
      value: undefined,
      values: undefined,
      startDate: undefined,
      endDate: undefined,
      minValue: undefined,
      maxValue: undefined
    };
    onConditionsChange([...conditions, newCondition]);
  };

  const updateCondition = (id: string, updates: Partial<Condition>) => {
    onConditionsChange(
      conditions.map(condition =>
        condition.id === id ? { ...condition, ...updates } : condition
      )
    );
  };

  const removeCondition = (id: string) => {
    onConditionsChange(conditions.filter(condition => condition.id !== id));
  };

  const getOptionsForType = (type: ConditionType) => {
    switch (type) {
      case 'sex':
        return availableOptions.sexTypes.map(item => ({
          value: item.value,
          label: item.description_es
        }));
      case 'channel':
        return availableOptions.channels.map(item => ({
          value: item.id,
          label: `${item.businessUnit?.name} - ${item.type.descriptionEs}`
        }));
      case 'brands':
        return availableOptions.brands.map(item => ({
          value: item.id,
          label: item.name
        }));
      case 'categories':
        return availableOptions.categories.map(item => ({
          value: item.id,
          label: item.name
        }));
      case 'level':
        return availableOptions.levels.map(item => ({
          value: item.id,
          label: item.name
        }));
      case 'city':
        return (availableOptions.statesByCountry || []).map(item => ({
          value: item.id,
          label: item.name
        }));
      default:
        return [];
    }
  };

  const renderConditionValue = (condition: Condition) => {
    const config = CONDITION_CONFIGS[condition.type];
    const options = getOptionsForType(condition.type);

    switch (config.valueType) {
      case 'select':
        return (
          <Select
            placeholder={config.placeholder}
            value={condition.value}
            onChange={(value) => updateCondition(condition.id, { value })}
            style={{ minWidth: 200 }}
          >
            {options.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );

      case 'multiSelect':
        const handleSearch = (value: string) => {
          if (condition.type === 'brands' && onSearchBrands) {
            onSearchBrands(value);
          } else if (condition.type === 'categories' && onSearchCategories) {
            onSearchCategories(value);
          }
        };

        return (
          <Select
            mode="multiple"
            placeholder={config.placeholder}
            value={condition.values}
            onChange={(values) => updateCondition(condition.id, { values })}
            onSearch={handleSearch}
            showSearch
            filterOption={(input, option) =>
              (option?.children?.toString() || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ minWidth: 200 }}
          >
            {options.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );

      case 'number':
        return (
          <InputNumber
            placeholder={config.placeholder}
            value={condition.value}
            onChange={(value) => updateCondition(condition.id, { value: value ?? undefined })}
            min={config.min}
            max={config.max}
            style={{ minWidth: 200 }}
          />
        );

      case 'numberRange':
        return (
          <Space>
            <InputNumber
              placeholder="Mínimo"
              value={condition.minValue}
              onChange={(value) => updateCondition(condition.id, { minValue: value ?? undefined })}
              min={config.min}
              max={config.max}
              style={{ width: 100 }}
            />
            <span>-</span>
            <InputNumber
              placeholder="Máximo"
              value={condition.maxValue}
              onChange={(value) => updateCondition(condition.id, { maxValue: value ?? undefined })}
              min={config.min}
              max={config.max}
              style={{ width: 100 }}
            />
          </Space>
        );

      case 'dateRange':
        return (
          <Space direction="vertical" size={8}>
            <Space>
              <span>{t("ClientSegment-FromDate")}:</span>
              <DatePicker
                placeholder={t("ClientSegment-FromDate")}
                value={condition.startDate ? moment(condition.startDate) : null}
                onChange={(date) =>
                  updateCondition(condition.id, {
                    startDate: date?.format("YYYY-MM-DD"),
                  })
                }
                style={{ minWidth: 160 }}
              />
            </Space>
            {!condition.untilPresent && (
              <Space>
                <span>{t("Common-End")}:</span>
                <DatePicker
                  placeholder={t("Common-End")}
                  value={condition.endDate ? moment(condition.endDate) : null}
                  onChange={(date) =>
                    updateCondition(condition.id, {
                      endDate: date?.format("YYYY-MM-DD"),
                      untilPresent: false,
                    })
                  }
                  style={{ minWidth: 160 }}
                />
              </Space>
            )}
            <Checkbox
              checked={!!condition.untilPresent}
              onChange={(e) =>
                updateCondition(condition.id, {
                  untilPresent: e.target.checked,
                  endDate: e.target.checked ? undefined : condition.endDate,
                })
              }
            >
              {t("ClientSegment-UntilPresent")}
            </Checkbox>
          </Space>
        );

      case 'purchasesInPeriod':
        return (
          <Space direction="vertical">
            <Space>
              <span>Compras:</span>
              <InputNumber
                placeholder="Cantidad"
                value={condition.value}
                onChange={(value) => updateCondition(condition.id, { value: value ?? undefined })}
                min={0}
                style={{ width: 120 }}
              />
            </Space>
            <Space>
              <span>En los últimos:</span>
              <InputNumber
                placeholder="Días"
                value={condition.minValue}
                onChange={(value) => updateCondition(condition.id, { minValue: value ?? undefined })}
                min={1}
                style={{ width: 120 }}
              />
              <span>días</span>
            </Space>
          </Space>
        );

      case 'purchasesInDateRange':
        return (
          <Space direction="vertical" size={8}>
            <Space>
              <span>{t("ClientSegment-FromDate")}:</span>
              <DatePicker
                placeholder={t("ClientSegment-FromDate")}
                value={condition.startDate ? moment(condition.startDate) : null}
                onChange={(date) =>
                  updateCondition(condition.id, {
                    startDate: date?.format("YYYY-MM-DD"),
                  })
                }
                style={{ minWidth: 160 }}
              />
            </Space>
            {!condition.untilPresent && (
              <Space>
                <span>{t("Common-End")}:</span>
                <DatePicker
                  placeholder={t("Common-End")}
                  value={condition.endDate ? moment(condition.endDate) : null}
                  onChange={(date) =>
                    updateCondition(condition.id, {
                      endDate: date?.format("YYYY-MM-DD"),
                      untilPresent: false,
                    })
                  }
                  style={{ minWidth: 160 }}
                />
              </Space>
            )}
            <Checkbox
              checked={!!condition.untilPresent}
              onChange={(e) =>
                updateCondition(condition.id, {
                  untilPresent: e.target.checked,
                  endDate: e.target.checked ? undefined : condition.endDate,
                })
              }
            >
              {t("ClientSegment-UntilPresent")}
            </Checkbox>
          </Space>
        );

      case 'city':
        const countries = (availableOptions.countries || []).map(item => ({
          value: item.id,
          label: item.name
        }));
        const states = (availableOptions.statesByCountry || []).map(item => ({
          value: item.id,
          label: item.name
        }));
        
        return (
          <Space>
            <Select
              placeholder="Seleccionar país"
              value={condition.countryId}
              onChange={(countryId) => {
                if (onFetchStatesByCountry) {
                  onFetchStatesByCountry(countryId);
                }
                updateCondition(condition.id, { 
                  countryId,
                  value: undefined // Reset city when country changes
                });
              }}
              style={{ minWidth: 150 }}
              allowClear
            >
              {countries.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {condition.countryId && (
              <Select
                placeholder="Seleccionar ciudad"
                value={condition.value}
                onChange={(value) => updateCondition(condition.id, { value })}
                style={{ minWidth: 150 }}
                allowClear
              >
                {states.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Space>
        );

      default:
        return null;
    }
  };

  const renderCondition = (condition: Condition) => {
    const config = CONDITION_CONFIGS[condition.type];

    return (
      <Card key={condition.id} size="small" className={styles.conditionCard}>
        <Space align="center" wrap>
          <span className={styles.conditionLabel}>
            {config.label}{config.valueType === 'dateRange' ? ':' : ''}
          </span>
          

          {renderConditionValue(condition)}

          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeCondition(condition.id)}
            size="small"
          />
        </Space>
      </Card>
    );
  };

  const availableConditionTypes = Object.values(CONDITION_CONFIGS);
  
  const usedConditionTypes = conditions.map(condition => condition.type);
  const availableTypes = availableConditionTypes.filter(
    config => !usedConditionTypes.includes(config.type)
  );

  return (
    <div className={styles.conditionBuilder}>
      <div className={styles.conditionsList}>
        {conditions.map(renderCondition)}
      </div>
      
      {availableTypes.length > 0 && (
        <div className={styles.addConditionSection}>
          <span className={styles.addLabel}>Agregar condición:</span>
          <Space wrap>
            {availableTypes.map(config => (
              <Button
                key={config.type}
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => addCondition(config.type)}
                size="small"
              >
                {config.label}
              </Button>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};

export default ClientSegmentConditionBuilder;
